import React, { useContext, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import mainColors from '../../../themes/main-colors';
import { useTranslation } from '../../../hooks/useTranslation';
import useFormState from '../../../hooks/useFormState';
import { FieldErrors } from '../../../hooks/useFormState.types';
import { useMutation } from '@apollo/react-hooks';
import {
  CancelEnergyConsultationRequest,
  CancelEnergyConsultationResponse,
  MutationCancelEnergyConsultationArgs,
} from '../../../__generated__/pge-types';
import { cancelEnergyConsultationMutation } from '../queries';
import { NotificationsContext } from '../../../providers/NotificationsProvider';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogPaper: {
      width: 560,
      maxWidth: '95%',
      borderRadius: 12,
    },
    header: {
      backgroundColor: mainColors.modalBlue,
      padding: theme.spacing(2, 3),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: 18,
      fontWeight: 700,
    },
    closeButton: {
      backgroundColor: mainColors.gray97,
      padding: 6,
    },
    content: {
      padding: theme.spacing(3),
    },
    warningContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(4),
      '& p': {
        margin: 0,
      },
    },
    warningBar: {
      width: 4,
      minHeight: 44,
      backgroundColor: mainColors.warning,
      borderRadius: 2,
      marginRight: theme.spacing(2),
    },
    warningText: {
      fontSize: 16,
      lineHeight: 1.4,
    },
    selectField: {
      marginBottom: theme.spacing(3),
    },
    notesField: {
      '& .MuiOutlinedInput-root': {
        alignItems: 'flex-start',
      },
    },
    characterCount: {
      textAlign: 'right',
      color: mainColors.lightGray4,
      fontSize: 13,
      marginTop: theme.spacing(1),
    },
    footer: {
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${mainColors.disableColor}`,
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '16px',
    },
    cancelButton: {
      minWidth: 120,
      color: mainColors.sparkBlue,
      borderColor: mainColors.sparkBlue,
      textTransform: 'none',
      fontWeight: 600,
    },
    submitButton: {
      minWidth: 120,
      backgroundColor: mainColors.orange,
      color: mainColors.white,
      textTransform: 'none',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: mainColors.orange,
      },
    },
  }),
);

type Context = {
  t: (s: string) => string;
};

function validate(data: CancelEnergyConsultationRequest, context: Context) {
  const errors: FieldErrors<CancelEnergyConsultationRequest> = {};
  if (!data.reasonForCancellation) {
    errors.reasonForCancellation = `${context.t('ENTER_THE')} ${context.t(
      'CANCEL_ENERGY_CONSULTATION_REASON',
    )}`;
  }
  return errors;
}

export interface CancelConsultationModalProps {
  requestId: string;
  open: boolean;
  onClose: () => void;
}

const CancelConsultationModal: React.FC<CancelConsultationModalProps> = ({
  requestId,
  open,
  onClose,
}) => {
  const classes = useStyles();
  const { t, richT } = useTranslation();
  const { wrapWithLoader } = useWrapWithLoader();
  const notificationContext = useContext(NotificationsContext);

  const cancellationReasons = [
    t('ENERGY_CONSULTATION_CANCELLATION_REASON_1'),
    t('ENERGY_CONSULTATION_CANCELLATION_REASON_2'),
    t('ENERGY_CONSULTATION_CANCELLATION_REASON_3'),
    t('ENERGY_CONSULTATION_CANCELLATION_REASON_4'),
  ];
  const form = useFormState(
    { reasonForCancellation: '', additionalNotes: '', requestId },
    {
      validate,
      validationContext: {
        t,
      },
    },
  );

  useEffect(() => {
    void form.setValue('requestId', requestId);
    void form.setValue('reasonForCancellation', '');
    void form.setValue('additionalNotes', '');
  }, [requestId]);

  const [cancelEnergyConsultation] = useMutation<
    {
      cancelEnergyConsultation: CancelEnergyConsultationResponse;
    },
    MutationCancelEnergyConsultationArgs
  >(cancelEnergyConsultationMutation);

  const setNotification = (
    message: string,
    severity: 'error' | 'success' = 'success',
  ) => {
    notificationContext?.setState?.({
      isOpen: true,
      severity,
      message,
    });

    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const cancelConsultationRequest = wrapWithLoader(
    async (payload: CancelEnergyConsultationRequest) => {
      try {
        const { data } = await cancelEnergyConsultation({
          variables: {
            payload,
          },
          refetchQueries: ['getEnergyConsultationRequests'],
        });
        if (data?.cancelEnergyConsultation?.success) {
          setNotification(
            t('ENERGY_CONSULTATION_REQUEST_CANCELLED_SUCCESSFULLY'),
          );
          onClose();
        }
      } catch (e) {
        setNotification(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'), 'error');
      }
    },
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <Box className={classes.header}>
        <Typography className={classes.title}>
          {t('CANCEL_ENERGY_CONSULTATION_HEADER')}
        </Typography>

        <IconButton
          size="small"
          onClick={onClose}
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent className={classes.content}>
        <Box className={classes.warningContainer}>
          <Box className={classes.warningBar} />

          <Typography className={classes.warningText} component={'div'}>
            {richT('CANCEL_ENERGY_CONSULTATION_WARNING')}
          </Typography>
        </Box>

        <TextField
          select
          fullWidth
          variant="outlined"
          label={t('CANCEL_ENERGY_CONSULTATION_REASON')}
          className={classes.selectField}
          {...form.props('reasonForCancellation')}
        >
          {cancellationReasons.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          multiline
          label={t('CANCEL_ENERGY_CONSULTATION_NOTES')}
          rows={5}
          variant="outlined"
          placeholder={t('CANCEL_ENERGY_CONSULTATION_NOTES_PLACEHOLDER')}
          className={classes.notesField}
          inputProps={{ maxLength: 300 }}
          {...form.props('additionalNotes')}
        />

        <Typography className={classes.characterCount}>
          {form.values?.additionalNotes?.length || '0'} / 300
        </Typography>

        <Box className={classes.footer}>
          <Button
            variant="outlined"
            onClick={() => {
              form.reset();
              onClose();
            }}
            className={classes.cancelButton}
          >
            {t('GO_BACK')}
          </Button>

          <Button
            variant="contained"
            className={classes.submitButton}
            onClick={form.submit(cancelConsultationRequest)}
            disabled={!form.values.reasonForCancellation}
          >
            {t('SUBMIT')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CancelConsultationModal;
